import { CalendarDays, Download, Loader2 } from 'lucide-react';
import { Button } from '../../ui/button';
import { Calendar } from '../../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';

const PERIODS = [
    { id: 'month', label: 'Month' }, { id: 'quarter', label: 'Quarter' }, { id: 'ytd', label: 'Year to date' },
    { id: 'custom', label: 'Custom dates' }, { id: 'all', label: 'All time' },
];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const thisYear = new Date().getFullYear();
const YEARS = Array.from({ length: thisYear - 2023 }, (_, i) => thisYear - i);

const Pick = ({ value, onChange, options, className = 'w-36', testId }) => (
    <Select value={String(value)} onValueChange={onChange}>
        <SelectTrigger className={className} data-testid={testId}><SelectValue /></SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o.id} value={String(o.id)}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
);

const toIsoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fromIsoDate = (s) => (s ? new Date(`${s}T00:00:00`) : undefined);

const DateField = ({ value, onChange, placeholder, min, testId }) => (
    <Popover>
        <PopoverTrigger asChild>
            <Button variant="outline" className="w-40 justify-start font-normal" data-testid={testId}>
                <CalendarDays className="mr-2 h-4 w-4 text-violet-500" />
                {value ? fromIsoDate(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : placeholder}
            </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
            <Calendar mode="single" selected={fromIsoDate(value)} onSelect={(d) => d && onChange(toIsoDate(d))}
                disabled={(d) => Boolean(min) && d < fromIsoDate(min)} initialFocus />
        </PopoverContent>
    </Popover>
);

export const defaultFilters = () => {
    const now = new Date();
    return { period: 'month', year: now.getFullYear(), month: now.getMonth() + 1, quarter: Math.floor(now.getMonth() / 3) + 1, start: '', end: '', user_id: 'all' };
};

export const filtersToParams = (f) => {
    const params = { period: f.period };
    if (f.user_id !== 'all') params.user_id = f.user_id;
    if (['month', 'quarter', 'ytd'].includes(f.period)) params.year = f.year;
    if (f.period === 'month') params.month = f.month;
    if (f.period === 'quarter') params.quarter = f.quarter;
    if (f.period === 'custom') Object.assign(params, { start: f.start, end: f.end });
    return params;
};

export const ReportFilters = ({ filters, setFilters, team, onExport, exporting }) => {
    const set = (key, cast = (v) => v) => (value) => setFilters((f) => ({ ...f, [key]: cast(value) }));
    const years = YEARS.map((y) => ({ id: y, label: String(y) }));
    return (
        <div className="flex flex-wrap items-end gap-3 rounded-2xl border bg-card p-4" data-testid="report-filters">
            <Pick value={filters.period} onChange={set('period')} options={PERIODS} testId="reports-period-select" />
            {filters.period === 'month' && <Pick value={filters.month} onChange={set('month', Number)} options={MONTHS.map((m, i) => ({ id: i + 1, label: m }))} testId="reports-month-select" />}
            {filters.period === 'quarter' && <Pick value={filters.quarter} onChange={set('quarter', Number)} className="w-24" options={[1, 2, 3, 4].map((q) => ({ id: q, label: `Q${q}` }))} testId="reports-quarter-select" />}
            {['month', 'quarter', 'ytd'].includes(filters.period) && <Pick value={filters.year} onChange={set('year', Number)} className="w-28" options={years} testId="reports-year-select" />}
            {filters.period === 'custom' && (
                <>
                    <DateField value={filters.start} onChange={set('start')} placeholder="Start date" testId="reports-start-date" />
                    <span className="pb-2 text-sm text-muted-foreground">to</span>
                    <DateField value={filters.end} onChange={set('end')} placeholder="End date" min={filters.start} testId="reports-end-date" />
                </>
            )}
            <Pick value={filters.user_id} onChange={set('user_id')} className="w-52" testId="reports-user-select"
                options={[{ id: 'all', label: 'All recruiters' }, ...team.map((u) => ({ id: u.id, label: u.name }))]} />
            <Button variant="outline" onClick={onExport} disabled={exporting} className="ml-auto" data-testid="reports-export-csv-button">
                {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Export CSV
            </Button>
        </div>
    );
};
